import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import * as React from 'react';
import { Check } from 'office-ui-fabric-react/lib/components/Check';
import { PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import onMailListItemClickHandler from '../utils/onMailListItemClickHandler';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import PersonaControl from 'owa-persona/lib/components/PersonaControl';
import shouldTableShowCirclePersonas from 'owa-mail-list-actions/lib/utils/tables/shouldTableShowCirclePersonas';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import getIsChecked from '../selectors/getIsChecked';
import DisplayDensityMode from 'owa-service/lib/contract/DisplayDensityMode';
import { isSingleLineListView } from 'owa-mail-layout';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { touchHandler, ITouchHandlerParams } from 'owa-mail-touch-handler';

const styles = require('./MailListItem.scss');
const classNames = require('classnames/bind').bind(styles);

export interface PersonaCheckboxProps {
    tableViewId: string;
    isSelected: boolean;
    lastSender: SingleRecipientType;
    rowKey: string;
    lastDeliveryTimestamp: string;
    isUnauthenticatedSender: boolean;
    densityMode: DisplayDensityMode;
    styleSelectorAsPerUserSettings: string;
    isCondensedView?: boolean;
}

@observer
export default class PersonaCheckbox extends React.Component<PersonaCheckboxProps, any> {
    @computed
    get isChecked(): boolean {
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        return getIsChecked(tableView, this.props.rowKey, this.props.lastDeliveryTimestamp);
    }

    @computed
    get touchHandler() {
        const touchHandlerParams: ITouchHandlerParams = {
            onClick: this.onClick,
        };
        return touchHandler(touchHandlerParams);
    }

    render() {
        const {
            isSelected,
            lastSender,
            isUnauthenticatedSender,
            densityMode,
            styleSelectorAsPerUserSettings,
            isCondensedView,
        } = this.props;
        const isSingleLine = isSingleLineListView();
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        const personaCheckboxContainerClasses = classNames(
            styleSelectorAsPerUserSettings,
            styles.personaCheckboxContainer,
            !isSingleLine &&
                (isCondensedView
                    ? 'personaMarginThreeColumnCondensedView'
                    : 'personaMarginThreeColumn'),
            (isSelected || tableView.isInCheckedMode) && styles.showCheckbox // Otherwise show persona
        );

        const checkboxClasses = classNames(
            styles.checkbox,
            this.isChecked && styles.checkboxChecked
        );

        const checkBoxAriaProps: AriaProperties = {
            role: AriaRoles.checkbox,
            checked: this.isChecked,
        };

        // lastSender is undefined in rowModified notification payload, if the message is directly POSTed
        const lastSenderMailbox = lastSender ? lastSender.Mailbox : null;
        const showCirclePersonas = shouldTableShowCirclePersonas(tableView.tableQuery);

        return (
            <div
                className={personaCheckboxContainerClasses}
                {...this.touchHandler}
                onClick={this.onClick}
                tabIndex={-1}
                onDoubleClick={this.onDoubleClick}
                {...generateDomPropertiesForAria(checkBoxAriaProps)}>
                {showCirclePersonas && lastSenderMailbox !== null && (
                    <PersonaControl
                        name={lastSenderMailbox.Name}
                        emailAddress={lastSenderMailbox.EmailAddress}
                        size={densityMode === 'Compact' ? PersonaSize.size24 : PersonaSize.size28}
                        showUnknownPersonaCoin={isUnauthenticatedSender}
                        className={styles.persona}
                        mailboxType={lastSenderMailbox.MailboxType}
                    />
                )}
                <Check checked={this.isChecked} className={checkboxClasses} />
            </div>
        );
    }

    private onClick = evt => {
        evt.preventDefault();
        evt.stopPropagation();
        onMailListItemClickHandler(
            evt,
            MailListItemSelectionSource.MailListItemCheckbox,
            this.props.rowKey,
            this.props.tableViewId
        );
    };

    // There's a double click on the outer component so must prevent propagation here (this is a corner case but causes an id of null error)
    private onDoubleClick(evt) {
        evt.stopPropagation();
    }
}
